import { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  KeyboardSensor,
  MeasuringStrategy,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { restrictToFirstScrollableAncestor } from '@dnd-kit/modifiers'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import clsx from 'clsx'
import dayjs from 'dayjs'

import { Excel } from '@makyoapp/makyo-icon/colored'
import { Spreadsheet } from '@makyoapp/makyo-icon/mimetype'
import { MaterialIcon } from '@makyoapp/makyo-tw-ui'
import { Button, ButtonVariant, DD, DDItem, SearchBox, useTheme } from '@makyoapp/makyo-ui'

import { useDbClientContext } from '@/context/__project__/DbClientContext'
import { useCurrentOrgContext } from '@/context/CurrentOrgContext'
import { useSingleProjectContext } from '@/context/SingleProjectContext'
import { useGetTablesGroup } from '@/hooks/useGetTablesGroup'
import { useGridImportData } from '@/hooks/useGridImportData'
import { useIsProjectDb } from '@/hooks/useIsProjectDb'
import { ORG_DATABASE_ROUTE, ORG_ROUTE, PROJECT_ROUTE } from '@/shared/constants'
import { appIconKeys } from '@/shared/icons/constants'
import { useGridV2Context } from '@/components/GridV2/contexts/GridV2Context'
import { RenderTableIcon, TableIconMap } from '@/components/RenderTableIcon'

function DraggableItem({
  children,
  id,
  isGroup,
  isChild,
}: {
  children: (listeners: any, active?: boolean) => React.ReactNode
  id: string
  isGroup?: boolean
  isChild?: boolean
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    data: { isGroup, isChild },
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  return (
    <div className='sortable-item' ref={setNodeRef} style={style} {...attributes}>
      {children(listeners, isDragging)}
    </div>
  )
}

const measuring = {
  droppable: {
    strategy: MeasuringStrategy.Always,
  },
}

/**
 * @todo setImportState
 */
export function LayoutTablesDropdown() {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const { state: themeState } = useTheme()

  const { currentOrg } = useCurrentOrgContext()
  const { project } = useSingleProjectContext()
  const isProject = useIsProjectDb()

  const { dbId, setShowSnapshotTable, setShowQueryTable, setShowCreateTableOptions, snapshotLoadingSet, dbClient } =
    useDbClientContext()
  const { activeTable, table } = useGridV2Context()
  const { startImport } = useGridImportData()

  // access the grouped tables
  const [groupedTables] = useGetTablesGroup()

  const snapshotLoading = snapshotLoadingSet.has(table?.id ?? '')

  const [show, setShow] = useState(false)
  const [search, setSearch] = useState('')
  const [isLoadingRefresh, setIsLoadingRefresh] = useState(false)
  const [activeId, setActiveId] = useState<string | null>(null)

  useEffect(() => {
    const cb = (e: any) => {
      if (e?.target && e.target.closest?.('#mk-tbl-menu')) {
        if (e.target.className?.includes?.('mk-dd-link')) {
          setShow(false)
        }
      }
    }
    window.addEventListener('click', cb)

    return () => {
      window.removeEventListener('click', cb)
    }
  }, [setShow])

  const handleStartImport = (fileType: 'csv' | 'excel') => {
    startImport(dbId, fileType)
  }

  const handleRefreshQuery = async () => {
    if (!activeTable) return
    setIsLoadingRefresh(true)

    if (activeTable.isQuery) {
      await activeTable.RefreshQuery()
    }
    window.dispatchEvent(new CustomEvent('db-client-reload'))
    setIsLoadingRefresh(false)
  }

  //--------------------------------------------------------------------------------------//
  //                             Drag and Drop (Grouped)                                  //
  //--------------------------------------------------------------------------------------//

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        delay: 0,
        tolerance: 4, // require 4px to activate drag movement
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  const activeItem = useMemo(
    () => (activeId ? groupedTables.find((item) => item.id === activeId) : null),
    [activeId, groupedTables]
  )
  const sortedIds = useMemo(() => groupedTables.map((item) => item.id), [groupedTables])

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string)
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    setActiveId(null)

    if (!over || active.id === over.id) {
      return
    }

    const activeItem = groupedTables.find((item) => item.id === active.id)
    const overItem = groupedTables.find((item) => item.id === over.id)

    if (!activeItem || !overItem) {
      return
    }

    // Prevent dropping a child outside of its parent group or a group into a child
    if (activeItem.parent && activeItem.parent !== overItem.parent && overItem.parent) {
      return
    }
    if (!activeItem.parent && overItem.parent) {
      return
    }

    const oldIndex = groupedTables.findIndex((item) => item.id === active.id)
    const newIndex = groupedTables.findIndex((item) => item.id === over.id)

    dbClient?.ReorderTable(active.id as string, newIndex, oldIndex)
  }

  const filteredTables = useMemo(() => {
    if (!search) return groupedTables

    const parents = groupedTables.filter(
      (item) => !item.parent && item.name.toLowerCase().includes(search.toLowerCase())
    )
    const children = groupedTables.filter(
      (item) => item.parent && item.name.toLowerCase().includes(search.toLowerCase())
    )
    const parentsOfChildren = groupedTables.filter((item) => children.some((c) => c.parent === item.id))

    return [...new Set([...parents, ...parentsOfChildren, ...children])]
  }, [groupedTables, search])

  return (
    <>
      <DD
        id='mk-tbl-dd'
        show={show}
        onToggle={() => setShow(!show)}
        menuClassName='sdd-menu-subc mk-ag-sdd-fixed-sub'
        menuNoPad
        menuDefaultSize={false}
        menuWrapperClassName='w-[260px]'
        placement='bottom-start'
        toggleClassName='max-w-[250px]'
        toggle={(_active, toggleProps) => {
          return (
            <div title={table?.name} className={clsx('sdd-toggle', 'sdd-toggle-normal !gap-3')}>
              <div {...toggleProps} className='flex items-center gap-2 truncate'>
                <RenderTableIcon table={table} isPlaceholder={!table || table?.id === ''} />
                <span className='txt'>{table?.name}</span>
              </div>
              <MaterialIcon size={16} icon='keyboard_arrow_down' />
            </div>
          )
        }}
      >
        <div id='mk-tbl-menu' className='mk-tbl-menu mk-scroll border-b bdr font-normal'>
          <div className='w-full'>
            <SearchBox
              id='search-table'
              value={search}
              onSearch={setSearch}
              onClearSearch={() => setSearch('')}
              noBorder
              className='mk-tbl-menu-search border-b w-full bdr'
            />
          </div>

          <div className='border-b bdr'>
            <DD
              className='w-full'
              id='mk-new-table'
              toggleNoButton
              toggle={(active, toggleProps) => {
                return (
                  <button {...toggleProps} className='mk-ddt mk-new-table w-full cursor-pointer'>
                    <div className='flex-c w-full'>
                      <MaterialIcon size={16} icon='add' />
                      <span>{t('New Table')}</span>
                    </div>
                    <MaterialIcon size={16} icon='keyboard_arrow_right' />
                  </button>
                )
              }}
              placement={themeState.dir === 'rtl' ? 'left' : 'right'}
              menuClassName='sdd-menu-subc'
            >
              <DDItem className='sdd-item flex-c' onClick={() => setShowCreateTableOptions(true)}>
                <MaterialIcon size={16} icon={appIconKeys.table} className='shrink-0' />
                <div>{t('New Table')}</div>
              </DDItem>
              <hr className='hr' />
              <div className='text-light text-m-sm px-4 py-2'>{t('Quick import from')}</div>
              <DDItem className='sdd-item flex-c' onClick={() => handleStartImport('csv')}>
                <Spreadsheet width={16} height={16} />
                <div>CSV</div>
              </DDItem>
              <DDItem className='sdd-item flex-c' onClick={() => handleStartImport('excel')}>
                <Excel width={16} height={16} />
                <div>Microsoft Excel</div>
              </DDItem>
            </DD>
          </div>

          <div className='max-h-[300px] overflow-y-auto overflow-x-hidden'>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              measuring={measuring}
              modifiers={[restrictToFirstScrollableAncestor]}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              onDragCancel={() => setActiveId(null)}
            >
              <SortableContext items={sortedIds} strategy={verticalListSortingStrategy}>
                {filteredTables.map((item) => (
                  <DraggableItem key={item.id} id={item.id}>
                    {(listeners, isDragging) => (
                      <div
                        className={clsx(
                          'flex cursor-pointer items-center rounded-sm py-2.5 hover:bg-zinc-200 dark:hover:bg-slate-900',
                          'select-none',
                          item.parent && 'pl-5',
                          activeItem && item.parent === activeItem.id ? 'hidden' : 'flex',
                          activeTable && activeTable.id === item.id ? 'bg-zinc-100 dark:bg-slate-700' : ''
                        )}
                        onClick={() => {
                          const path = isProject
                            ? `${PROJECT_ROUTE}/${project?.id}/${ORG_DATABASE_ROUTE}/${item.id}`
                            : `${ORG_ROUTE}/${currentOrg?.id}/${ORG_DATABASE_ROUTE}/${dbId}/${item.id}`
                          navigate(path)
                          setShow(false)
                        }}
                      >
                        <div {...listeners} className='shrink-0 drag-handle cursor-grab flex-cc px-2'>
                          <MaterialIcon icon='drag_indicator' size={16} className='text-light' />
                        </div>

                        <div className='pr-2 flex-1 flex items-center gap-x-2 truncate'>
                          {TableIconMap[item.type]}
                          <span className='flex-1 text-left truncate'>{item.name}</span>
                          {!!item.childrenCount && item.childrenCount > 0 && (
                            <span className='text-m-sm text-light'>{item.childrenCount} views</span>
                          )}
                        </div>
                      </div>
                    )}
                  </DraggableItem>
                ))}
              </SortableContext>

              {createPortal(
                <DragOverlay>
                  {activeId && activeItem ? (
                    <DraggableItem id={activeId}>
                      {() => (
                        <div
                          className={clsx(
                            'flex items-center rounded-sm py-2.5 shadow-md bg-zinc-200 dark:bg-slate-900 mk-text text-m-m',
                            activeItem.parent && 'pl-5'
                          )}
                        >
                          <div className='shrink-0 drag-handle cursor-grab flex-cc px-2'>
                            <MaterialIcon icon='drag_indicator' size={16} className='text-light' />
                          </div>

                          <div className='pr-2 flex-1 flex items-center gap-x-2 truncate'>
                            {TableIconMap[activeItem.type]}
                            <span className='text-brand-500 flex-1 text-left truncate'>{activeItem.name}</span>
                            {!!activeItem.childrenCount && activeItem.childrenCount > 0 && (
                              <span className='text-m-sm text-light'>{activeItem.childrenCount} views</span>
                            )}
                          </div>
                        </div>
                      )}
                    </DraggableItem>
                  ) : null}
                </DragOverlay>,
                document.body
              )}
            </DndContext>
          </div>
        </div>
      </DD>

      {activeTable && (
        <div className='pl-2 flex items-center gap-4 shrink-0'>
          {(activeTable.isSnapshot || activeTable.isQuery) && (
            <>
              {snapshotLoading ? (
                <Button
                  variant={ButtonVariant.Gray}
                  icon={<span className='loading spin w-4! h-4!' />}
                  title={t('Re-computing snapshot source table. Please do not leave this page')}
                  disabled
                >
                  {t(`Updating ${activeTable.isSnapshot ? 'snapshot' : 'query'}`)}
                </Button>
              ) : (
                <Button
                  variant={ButtonVariant.Outlined}
                  icon={<MaterialIcon size={15} icon='settings' />}
                  onClick={() => {
                    if (activeTable.isSnapshot) {
                      setShowSnapshotTable('edit')
                    } else {
                      setShowQueryTable('edit')
                    }
                  }}
                >
                  {t(`${activeTable.isSnapshot ? 'Snapshot' : 'Query'} Settings`)}
                </Button>
              )}
            </>
          )}

          <Button
            variant={isLoadingRefresh ? ButtonVariant.Gray : ButtonVariant.Outlined}
            icon={isLoadingRefresh ? <span className='loading spin' /> : <MaterialIcon size={15} icon='refresh' />}
            disabled={isLoadingRefresh}
            onClick={handleRefreshQuery}
          >
            {isLoadingRefresh ? t('Refreshing...') : t('Refresh')}
          </Button>
        </div>
      )}

      {table && table.isSnapshot && (
        <div className='text-light !font-normal text-m-m px-2 shrink-0'>
          Last updated {dayjs(table.snapshotSettings?._Process._LastRanAt).fromNow()}
        </div>
      )}

      {table && table?.isQuery && !isLoadingRefresh && (
        <div className='text-light !font-normal text-m-m px-2 shrink-0'>
          Last updated {dayjs(table.snapshotSettings?._Process._LastRanAt).fromNow()}
        </div>
      )}
    </>
  )
}
